import { type ComponentController as BpComponentController, DisplayMode } from '@rta/rta-blueprint-component';

export type ThreeDNavigationOverride = {
	displayMode: DisplayMode;
	islandId: string | null;
	/** null means the click was on the countertop — the menus read it that way */
	panelId: string | null;
};

/**
 * The design's blueprint controller with `navigation` answering for what was clicked in 3D
 * instead of whatever the 2D designer was last showing. Everything else — kitchen, readonly,
 * options, the publish methods the menu actions call — is the real controller, so an action
 * behaves exactly as it does in the designer. Writes still land on the real navigation, so
 * "Edit Island" takes the 2D view to the island that was clicked.
 */
export const threeDMenuController = (controller: BpComponentController, override: ThreeDNavigationOverride): BpComponentController => {
	const { navigation } = controller;

	const navigationProxy = new Proxy(navigation, {
		get: (target, property, _receiver) => {
			if (property in override) return override[property as keyof ThreeDNavigationOverride];
			// Reads resolve against the target, never the proxy — a receiver swap breaks #private fields
			const value = Reflect.get(target, property, target);
			return typeof value === 'function' ? value.bind(target) : value;
		},
		set: (target, property, value) => Reflect.set(target, property, value, target)
	});

	return new Proxy(controller, {
		get: (target, property, _receiver) => {
			if (property === 'navigation') return navigationProxy;
			const value = Reflect.get(target, property, target);
			return typeof value === 'function' ? value.bind(target) : value;
		},
		set: (target, property, value) => Reflect.set(target, property, value, target)
	});
};
